import React from 'react';
import { Form, message, Tabs } from 'antd';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Button } from 'src/components';
import FormModalStyle from './FormModalStyle';
import { useAppDispatch, useAppSelector } from 'src/stores';
import {
  CheckboxField,
  InputField,
  SelectField,
  TextAreaField,
  UploadImage,
} from 'src/components/form';
import {
  COMPONENT_OPTIONS,
  COMPONENT_VALUES,
  LIST_LANGUAGES,
} from 'src/constants/form';
import { upperCaseFirstWord } from 'src/utils/string';
import {
  createPageSessionAction,
  updatePageSessionAction,
} from 'src/stores/screens/pageSession/pageSession.action';
import classNames from 'classnames';

interface FormModalProps {
  isModalFormOpen: boolean;
  handleCancel: () => void;
  handleOk: () => void;
  pageId?: string;
  currentRecord?: PageSession.PageSessionType | null;
  fetchData: (page?: number) => void;
  currentPage?: number;
}

const FormModal: React.FC<FormModalProps> = ({
  isModalFormOpen,
  handleCancel,
  handleOk,
  pageId,
  currentRecord,
  fetchData,
  currentPage,
}) => {
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector((state) => state.page);

  const validationSchema = yup.object().shape({
    pageId: yup.string().required('Page ID is required'),
    component: yup.string().required('Component is required'),
    titleVi: yup
      .string()
      .nullable()
      .trim()
      .max(100, 'Title must be at most 100 characters')
      .required('Title is required'),
    descriptionVi: yup
      .string()
      .nullable()
      .trim()
      .max(2000, 'Description must be at most 2000 characters'),
    titleEn: yup
      .string()
      .nullable()
      .trim()
      .max(100, 'Title must be at most 100 characters')
      .required('Title is required'),
    descriptionEn: yup
      .string()
      .nullable()
      .trim()
      .max(2000, 'Description must be at most 2000 characters'),
    titleJa: yup
      .string()
      .nullable()
      .trim()
      .max(100, 'Title must be at most 100 characters')
      .required('Title is required'),
    descriptionJa: yup
      .string()
      .nullable()
      .trim()
      .max(2000, 'Description must be at most 2000 characters'),
    titleKo: yup
      .string()
      .nullable()
      .trim()
      .max(100, 'Title must be at most 100 characters')
      .required('Title is required'),
    descriptionKo: yup
      .string()
      .nullable()
      .trim()
      .max(2000, 'Description must be at most 2000 characters'),
  });

  const initialValues: PageSession.PageSessionPostDetailParams =
    currentRecord?.id
      ? { ...currentRecord, pageId }
      : {
          pageId,
          component: '',
          titleVi: '',
          titleEn: '',
          titleJa: '',
          titleKo: '',
          descriptionVi: '',
          descriptionEn: '',
          descriptionJa: '',
          descriptionKo: '',
          fileUrl: '',
          isShow: true,
        };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues,
    onSubmit: async (values: PageSession.PageSessionPostDetailParams) => {
      const result: any = currentRecord?.id
        ? await dispatch(
          updatePageSessionAction({
            data: { ...values, id: currentRecord.id, pageId },
          })
        )
        : await dispatch(
          createPageSessionAction({ data: { ...values, pageId } })
        );
      if (result?.payload?.error) {
        message.error(result?.payload?.message);
      } else {
        message.success(
          currentRecord?.id ? 'Update successful' : 'Creation successful'
        );
        formik.resetForm({});
        fetchData(currentRecord?.id ? currentPage : 1);
        handleOk();
      }
    },
  });

  const onClose = () => {
    formik.resetForm({});
    handleCancel();
  };

  const renderListTabItems = LIST_LANGUAGES.map((item) => {
    const fieldNameTitle = 'title' + upperCaseFirstWord(item);
    const fieldNameDescription = 'description' + upperCaseFirstWord(item);

    const isErrors =
      formik.errors[fieldNameTitle] || formik.errors[fieldNameDescription];
    return (
      <Tabs.TabPane
        tabKey={item}
        tab={
          <span className={classNames({ error: isErrors })}>
            {item.toUpperCase()}
            <span style={{ color: 'red' }}> *</span>
          </span>
        }
        key={item}
      >
        <InputField
          className="form-control"
          label="Title"
          field={formik.getFieldProps(fieldNameTitle)}
          setFieldValue={formik.setFieldValue}
          inputProps={{
            size: 'middle',
            placeholder: 'Title',
          }}
          error={formik.errors[fieldNameTitle]}
          touched={formik.touched[fieldNameTitle]}
          required
        />
        <TextAreaField
          className="form-control"
          label="Description"
          field={formik.getFieldProps(fieldNameDescription)}
          setFieldValue={formik.setFieldValue}
          textAreaProps={{
            size: 'middle',
            placeholder: 'Description',
            autoSize: { minRows: 4, maxRows: 10 },
          }}
          error={formik.errors[fieldNameDescription]}
          touched={formik.touched[fieldNameDescription]}
        />
      </Tabs.TabPane>
    );
  });

  return (
    <FormModalStyle
      title={currentRecord?.id ? 'Update' : 'Create'}
      visible={isModalFormOpen}
      onCancel={onClose}
      className="modal-form"
      width={800}
    >
      <Form
        name="edit-form"
        className="setting-form"
        layout="vertical"
        onFinish={() => {
          formik.handleSubmit();
        }}
      >
        <SelectField
          label="Component"
          optionsSelect={COMPONENT_OPTIONS}
          field={formik.getFieldProps('component')}
          setFieldValue={formik.setFieldValue}
          className="form-control"
          error={formik.errors.component}
          touched={formik.touched.component}
          placeholder="Component"
          required
        />
        <CheckboxField
          className="form-control checkbox"
          label="Show"
          field={formik.getFieldProps('isShow')}
          setFieldValue={formik.setFieldValue}
          error={formik.errors.isShow}
          touched={formik.touched.isShow}
        />

        <UploadImage
          className="form-control"
          label={
            currentRecord?.component === COMPONENT_VALUES.BANNER
              ? 'Image/Video'
              : 'Image (Recommend 4:3)'
          }
          field={formik.getFieldProps('fileUrl')}
          file={formik.getFieldProps('fileUrl').value}
          fileType={formik.values.fileType}
          setFieldValue={formik.setFieldValue}
          error={formik.errors.fileUrl}
          touched={formik.touched.fileUrl}
          uploadVideo={currentRecord?.component === COMPONENT_VALUES.BANNER}
        />

        <hr />

        <Tabs defaultActiveKey="en">{renderListTabItems}</Tabs>
        {/* Button footer */}
        <div className="button-footer">
          <Button buttonType="cancel" onClick={onClose} width={100}>
            Close
          </Button>
          <Button htmlType="submit" width={100} disabled={isLoading}>
            {currentRecord?.id ? 'Update' : 'Create'}
          </Button>
        </div>
      </Form>
    </FormModalStyle>
  );
};

export default FormModal;
